import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { EntityIntegerSource } from "building-types/doc/entityIntegerSource";
import type { BuildingEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { EntityRelationship } from "common/graph/entityGraphTypes";
import type { AABB3 } from "geom/aabb3Types";
import type { RelationshipType } from "./buildingEntityIndex";
import { EntityIndex } from "./buildingEntityIndex";
import { NullEntityIntegerSource } from "./nullEntityIntegerSource";

export interface BuildingGenerationContextInterface {
  indexLoadBuildingEntityGraph(building: Pick<Building, "entityGraphNodes" | "entityGraphRelationships">): void;
  indexNodeHash(node: DocEntityHandle): string;
  indexRelationships(
    node: DocEntityHandle,
    predicate?: (relationship: EntityRelationship<string, RelationshipType>) => boolean,
  ): EntityRelationship<string, RelationshipType>[];
  indexNodeByHash(hash: string): DocEntityHandle | undefined;
  indexNodes(): DocEntityHandle[];
  indexNodeAABB(node: DocEntityHandle): AABB3 | undefined;
  indexAddNode(node: DocEntityHandle, aabb?: AABB3): DocEntityHandle;
  indexNodeByDocEntityHandle(handle: DocEntityHandle): DocEntityHandle | undefined;
  indexRelate(a: DocEntityHandle, type: RelationshipType, b: DocEntityHandle): void;
  indexSpatialSearch(aabb: AABB3, predicate?: (handle: DocEntityHandle) => boolean): DocEntityHandle[];
  indexBreadthFirstSearch(node: DocEntityHandle, depth?: number): DocEntityHandle[];
  setEntityIntegerSource(entityIntegerSource: EntityIntegerSource): void;
  gcEntityIntegerSource(doc: Doc, building: Building): void;
  getInteger(handle: DocEntityHandle): number;
  getBuildingEntityInteger(type: BuildingEntityType, id: string, handle?: DocEntityHandle): number;
  allocate(handle: DocEntityHandle): number;
}

class BuildingGenerationContextImpl implements BuildingGenerationContextInterface {
  private _entityIntegerSource: EntityIntegerSource;
  private _index: EntityIndex;
  private _currentStageId: string | undefined = undefined;
  private _handlesByStage: Map<string, DocEntityHandle[]>;

  constructor(entityIntegerSource: EntityIntegerSource) {
    this._entityIntegerSource = entityIntegerSource;
    this._index = new EntityIndex();
    this._handlesByStage = new Map();
  }

  startStage(stageId: string) {
    if (this._currentStageId) {
      console.warn(`stage ${this._currentStageId} was not finalized before starting stage ${stageId}`);
    }
    if (this._handlesByStage.has(stageId)) {
      const handles = this._handlesByStage.get(stageId) ?? [];
      for (const handle of handles) {
        this._index.deleteNode(handle);
      }
    }
    this._currentStageId = stageId;
    this._handlesByStage.set(stageId, []);
  }

  endStage() {
    this._currentStageId = undefined;
  }

  indexLoadBuildingEntityGraph(building: Pick<Building, "entityGraphNodes" | "entityGraphRelationships">) {
    this._index.loadBuildingEntityGraph(building);
  }

  indexNodeHash(node: DocEntityHandle) {
    return this._index.nodeHash(node);
  }

  indexRelationships(
    node: DocEntityHandle,
    predicate?: (relationship: EntityRelationship<string, RelationshipType>) => boolean,
  ) {
    return this._index.relationships(node, predicate);
  }

  indexNodeByHash(hash: string) {
    return this._index.nodeByHash(hash);
  }

  indexNodes() {
    return this._index.nodes();
  }

  indexNodeAABB(node: DocEntityHandle) {
    return this._index.nodeAABB(node);
  }

  indexAddNode(node: DocEntityHandle, aabb?: AABB3) {
    const handle = this._index.addNode(node, aabb);
    const currentStageId = this._currentStageId;
    if (currentStageId !== undefined) {
      this._handlesByStage.get(currentStageId)?.push(handle);
    }
    return handle;
  }

  indexNodeByDocEntityHandle(handle: DocEntityHandle) {
    return this._index.nodeByDocEntityHandle(handle);
  }

  indexRelate(a: DocEntityHandle, type: RelationshipType, b: DocEntityHandle) {
    return this._index.relate(a, type, b);
  }

  indexSpatialSearch(aabb: AABB3, predicate?: (handle: DocEntityHandle) => boolean) {
    return this._index.spatialSearch(aabb, predicate);
  }

  indexBreadthFirstSearch(node: DocEntityHandle, depth: number = 1) {
    return this._index.breadthFirstSearch(node, depth);
  }

  setEntityIntegerSource(entityIntegerSource: EntityIntegerSource) {
    this._entityIntegerSource = entityIntegerSource;
  }

  gcEntityIntegerSource(doc: Doc, building: Building) {
    this._entityIntegerSource.gc(doc, building);
  }

  getInteger(handle: DocEntityHandle) {
    return this._entityIntegerSource.getInteger(handle);
  }

  getBuildingEntityInteger(type: BuildingEntityType, id: string, handle?: DocEntityHandle) {
    return this._entityIntegerSource.getBuildingEntityInteger(type, id, handle);
  }

  allocate(handle: DocEntityHandle): number {
    return this._entityIntegerSource.allocate(handle);
  }
}

export const BuildingGenerationContext = new BuildingGenerationContextImpl(NullEntityIntegerSource);
